// ============================================================================
// OINP employer checker results.
// Three-state verdict (appears eligible / needs verification / not eligible)
// with the per-requirement reason for anything that isn't a clean pass.
// The verdict itself is free — capture sits on the detail (full PDF checklist
// / send to adviser), so completion rate survives but the employer, who is the
// actual gatekeeper of the nomination, becomes a traceable lead.
// ============================================================================

import React, { useState } from 'react';
import {
  Wrapper, StepTitle, Card, CardHeading, Note, GateList,
  TextInput, InlineRow, NextBtn, PrimaryBtn, GhostBtn, Disclaimer, ErrorText,
} from '../assessments/oinp/ui';
import { translator } from '../assessments/oinp/i18n';
import { EMPLOYER_CHECKLIST_PHASES } from '../assessments/oinp/rules';
import { printEmployerChecklist } from '../assessments/oinp/print';

const VERDICT_COPY = {
  'appears-eligible': { key: 'empVerdictEligible', color: '#16a34a' },
  'needs-verification': { key: 'empVerdictNeedsVerification', color: '#d97706' },
  'not-eligible': { key: 'empVerdictNotEligible', color: '#dc3545' },
};

// Short requirement labels — deliberately NOT the question prompts, two of
// which carry {revenueFloor}/{minEmployees} placeholders that have no values
// here and would render literally.
const REQUIREMENT_LABELS = {
  'location': 'empReqLocation',
  'years-in-business': 'empReqYears',
  'revenue': 'empReqRevenue',
  'employees': 'empReqEmployees',
  'esa-ohsa': 'empReqOrders',
  'job-offer-shape': 'empReqFullTime',
  'wage-level': 'empReqWage',
  'recruitment-waiver': 'empReqRecruitment',
  'cvor': 'empReqCvor',
};

// Reuse the candidate gate-list renderer: map each question onto the row shape
// it expects, carrying an explicit labelKey.
const toGateRows = items => items.map(item => ({
  id: item.id,
  labelKey: REQUIREMENT_LABELS[item.id] || item.id,
  status: item.status === 'unanswered' ? 'warn' : item.status,
  note: item.reasonKey,
}));

const OINPEmployerResults = ({
  result,
  onRequestChecklist,
  onSendToAdviser,
  onContinueToIntake,
  onRequestConsultation,
  brand = null,
  darkMode = false,
  scheme = 'blue',
  language = 'en',
}) => {
  const tr = translator(language);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [printError, setPrintError] = useState('');
  const [consultSent, setConsultSent] = useState(false);

  if (!result) return null;
  const verdict = VERDICT_COPY[result.verdict] || VERDICT_COPY['needs-verification'];

  const submitChecklist = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(tr('alertsInvalidEmail'));
      return;
    }
    setEmailError('');
    onRequestChecklist && onRequestChecklist(email, result);
    setSent(true);
  };

  // Print is always available — an employer shouldn't have to hand over an email
  // just to read their own to-do list.
  const handlePrint = () => {
    const opened = printEmployerChecklist({ result, tr, phases: EMPLOYER_CHECKLIST_PHASES, brand });
    setPrintError(opened ? '' : tr('printBlocked'));
  };

  return (
    <Wrapper>
      <StepTitle $darkMode={darkMode}>{tr('employerTitle')}</StepTitle>

      <Card $darkMode={darkMode}>
        <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.4, marginBottom: 10, color: verdict.color }}>
          {tr(verdict.key)}
        </div>
        <GateList gates={toGateRows(result.items)} darkMode={darkMode} tr={tr} />
      </Card>

      {result.tier && (
        <Note $darkMode={darkMode}>
          {tr('empTierNote', {
            revenueFloor: result.tier.revenueFloor,
            minEmployees: result.tier.minEmployees,
          })}
        </Note>
      )}

      {/* --------------------------------------------------------- capture -- */}
      <Card $darkMode={darkMode}>
        <CardHeading $darkMode={darkMode}>{tr('empChecklistCta')}</CardHeading>
        {sent ? (
          <Note $darkMode={darkMode} style={{ marginBottom: 0 }}>{tr('empCaptureDone')}</Note>
        ) : (
          <>
            <InlineRow>
              <TextInput
                $darkMode={darkMode}
                $scheme={scheme}
                type="email"
                value={email}
                placeholder={tr('empCaptureEmail')}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') submitChecklist(); }}
              />
              <NextBtn type="button" $darkMode={darkMode} $scheme={scheme} onClick={submitChecklist}>
                {tr('empCaptureSend')}
              </NextBtn>
            </InlineRow>
            {emailError && <ErrorText>{emailError}</ErrorText>}
            {onSendToAdviser && (
              <GhostBtn type="button" $darkMode={darkMode} onClick={() => onSendToAdviser(result)}>
                {tr('empAdviserCta')}
              </GhostBtn>
            )}
          </>
        )}
        <GhostBtn type="button" $darkMode={darkMode} onClick={handlePrint}>
          {tr('printChecklist')}
        </GhostBtn>
        {printError && <ErrorText>{printError}</ErrorText>}
      </Card>

      {/* Same mechanism as the candidate side: continuing to consultation hands
          the answers over as a conversation and the case goes through the normal
          analysis pipeline. */}
      {onContinueToIntake && (
        <Card $darkMode={darkMode}>
          <CardHeading $darkMode={darkMode}>{tr('consultHeading')}</CardHeading>
          <div style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 12, color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
            {tr('consultBody', { expert: brand?.expertName || tr('consultFallbackExpert') })}
          </div>
          <PrimaryBtn
            type="button"
            $darkMode={darkMode}
            $scheme={scheme}
            disabled={consultSent}
            onClick={() => { setConsultSent(true); onContinueToIntake(''); }}
          >
            {tr('continueCta')}
          </PrimaryBtn>
        </Card>
      )}

      <Disclaimer $darkMode={darkMode}>{tr('disclaimer')}</Disclaimer>
    </Wrapper>
  );
};

export default OINPEmployerResults;
